import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Archive, ShieldCheck } from "lucide-react";
import { ApiError } from "@/api/client";
import {
  maintenanceApi,
  type MaintenancePolicy,
  type MaintenancePreview,
} from "@/api/maintenanceClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { OperationalResetGuide } from "./OperationalResetGuide";

function localDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return localDate(d);
}

function fmtTime(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
}

function fmtRows(n: number): string {
  return n.toLocaleString("zh-CN");
}

export function MaintenanceTab() {
  const qc = useQueryClient();
  const policiesQ = useQuery({ queryKey: ["maintenance", "policies"], queryFn: maintenanceApi.policies });
  const historyQ = useQuery({ queryKey: ["maintenance", "history"], queryFn: maintenanceApi.history, refetchInterval: 30_000 });

  const executable = (policiesQ.data?.items ?? []).filter((p) => p.kind !== "readonly");
  const readonly = (policiesQ.data?.items ?? []).filter((p) => p.kind === "readonly");

  const [target, setTarget] = useState("usage_runtime");
  const [days, setDays] = useState(7);
  const [date, setDate] = useState(() => daysAgo(7));
  const [preview, setPreview] = useState<MaintenancePreview | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const policy: MaintenancePolicy | undefined = executable.find((p) => p.key === target) ?? executable[0];
  const isFuture = new Date(date + "T00:00:00") > new Date();

  const resetPreview = () => {
    setPreview(null);
    setErr(null);
    setMsg(null);
  };

  const chooseTarget = (key: string) => {
    const p = executable.find((x) => x.key === key);
    setTarget(key);
    setDays(p?.default_days ?? 7);
    setDate(daysAgo(p?.default_days ?? 7));
    resetPreview();
  };

  const chooseDays = (n: number) => {
    setDays(n);
    setDate(daysAgo(n));
    resetPreview();
  };

  const previewMut = useMutation({
    mutationFn: () => maintenanceApi.preview(target, date),
    onSuccess: (data) => {
      setPreview(data);
      setErr(null);
      setMsg(null);
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  const executeMut = useMutation({
    mutationFn: () => {
      if (!preview) throw new Error("请先预览");
      return maintenanceApi.execute(target, preview.before, confirmText);
    },
    onSuccess: (data) => {
      setConfirmOpen(false);
      setConfirmText("");
      setPreview(null);
      setErr(null);
      setMsg(`已清理 ${data.files != null ? `${data.files} 个文件` : `${fmtRows(data.rows)} 行`}；manifest: ${data.manifest}`);
      qc.invalidateQueries({ queryKey: ["maintenance", "history"] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  const confirmPhrase = `DELETE ${target}`;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(300px,.65fr)]">
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs leading-relaxed text-amber-900">
          <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" />运维清理</div>
          <p className="mt-1">
            数据库目标先归档 <code className="rounded bg-black/5 px-1">csv.gz</code> + SHA-256 + manifest，再删除；删除与 <code className="rounded bg-black/5 px-1">SYS_CLEANUP</code> 审计同事务。自定义时间不设最小保留期，但拒绝未来日期。
          </p>
        </div>

        <section className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="mb-3">
            <h2 className="text-sm font-semibold">清理计划</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">选择对象与保留期；预览只统计，不删除任何数据。</p>
          </div>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label>清理对象</Label>
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={policy?.key ?? target}
                onChange={(e) => chooseTarget(e.target.value)}
                disabled={policiesQ.isLoading}
              >
                {executable.map((p) => (
                  <option key={p.key} value={p.key} disabled={p.disabled}>
                    {p.label}{p.disabled ? `（${p.disabled_reason ?? "暂不可用"}）` : ""}
                  </option>
                ))}
              </select>
              <p className="text-[11px] leading-relaxed text-muted-foreground">{policy?.note}</p>
            </div>

            <div className="space-y-1">
              <Label>保留期</Label>
              <div className="flex flex-wrap gap-1.5">
                {(policy?.presets ?? []).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => chooseDays(n)}
                    className={`rounded-full border px-3 py-1 text-xs ${days === n ? "border-primary bg-primary/10 font-semibold text-primary" : "text-muted-foreground hover:bg-muted"}`}
                  >
                    {n} 天
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { setDays(0); resetPreview(); }}
                  className={`rounded-full border px-3 py-1 text-xs ${days === 0 ? "border-primary bg-primary/10 font-semibold text-primary" : "text-muted-foreground hover:bg-muted"}`}
                >
                  自定义
                </button>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Input
                  type="date"
                  className="max-w-[210px]"
                  value={date}
                  onChange={(e) => { setDate(e.target.value); setDays(0); resetPreview(); }}
                />
                <span className="text-[11px] text-muted-foreground">截止日期（不含当天）</span>
              </div>
              {isFuture && <p className="text-[11px] text-destructive">截止日期不能在未来。</p>}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => previewMut.mutate()}
                disabled={!policy || policy.disabled || !date || isFuture || previewMut.isPending}
              >
                {previewMut.isPending ? "预览中…" : "预览清理"}
              </Button>
              <Button
                variant="destructive"
                disabled={!preview || isFuture || previewMut.isPending}
                onClick={() => { setConfirmText(""); setConfirmOpen(true); setErr(null); }}
              >
                执行清理
              </Button>
              <span className="text-[11px] text-muted-foreground">执行前需输入确认词</span>
            </div>

            {err && <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
            {msg && <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">{msg}</div>}

            {preview && (
              <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
                <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                  <div><div className="text-muted-foreground">预计删除</div><div className="mt-0.5 font-semibold">{preview.files != null ? `${preview.files} 个文件` : `${fmtRows(preview.rows)} 行`}</div></div>
                  <div><div className="text-muted-foreground">截止时间</div><div className="mt-0.5 font-semibold">{preview.before.slice(0, 16).replace("T", " ")}</div></div>
                  <div className="col-span-2"><div className="text-muted-foreground">归档目录</div><div className="mt-0.5 break-all font-mono text-[11px]">{preview.archive_dir}</div></div>
                </div>
                {preview.tables && preview.tables.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {preview.tables.map((t) => (
                      <span key={t.table} className="rounded border bg-background px-2 py-0.5 font-mono text-[10px]">{t.table}: {fmtRows(t.rows)}</span>
                    ))}
                  </div>
                )}
                {preview.warnings?.map((w) => (
                  <div key={w} className="rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] leading-relaxed text-amber-900">{w}</div>
                ))}
              </div>
            )}
          </div>
        </section>

        <OperationalResetGuide />

        <section className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">最近运维审计</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">复用 /audit-logs?action=SYS_CLEANUP。</p>
            </div>
            <Archive className="h-4 w-4 text-muted-foreground" />
          </div>
          {historyQ.isLoading ? (
            <div className="py-4 text-xs text-muted-foreground">加载中…</div>
          ) : (historyQ.data?.items.length ?? 0) === 0 ? (
            <div className="py-4 text-center text-xs text-muted-foreground">暂无清理审计</div>
          ) : (
            <div className="space-y-2">
              {historyQ.data!.items.slice(0, 10).map((a) => (
                <div key={a.id} className="rounded-lg border px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant="success">{a.action}</Badge>
                    <span className="font-mono font-semibold">{a.target_key}</span>
                    <span className="text-muted-foreground">{a.operator_name}</span>
                    <span className="ml-auto text-[11px] text-muted-foreground">{fmtTime(a.occurred_at)}</span>
                  </div>
                  <div className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                    {a.before_state} → {a.after_state}
                    {a.note && <span className="ml-1 break-all">{a.note}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <aside className="space-y-4">
        <section className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="mb-2 flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold">执行前检查</h2>
          </div>
          <dl className="space-y-1.5 text-xs">
            <div className="flex justify-between gap-2"><dt className="text-muted-foreground">操作身份</dt><dd><Badge variant="warning">sys</Badge></dd></div>
            <div className="flex justify-between gap-2"><dt className="text-muted-foreground">归档目录</dt><dd className="text-right font-mono text-[10px]">{preview?.archive_dir ?? "预览后显示"}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-muted-foreground">审计写入</dt><dd>同事务</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-muted-foreground">最小保留期</dt><dd>无（需确认）</dd></div>
          </dl>
          <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-2 text-[11px] leading-relaxed text-blue-900">
            admin 看不到此 tab；直接调用 <code className="rounded bg-black/5 px-1">/sys/maintenance/*</code> 返回 403。前端隐藏不是权限边界。
          </div>
        </section>

        <section className="rounded-xl border bg-card p-4 shadow-sm">
          <h2 className="text-sm font-semibold">只读保留矩阵</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">只归档 / append-only / 配置类，不提供删除。</p>
          <div className="mt-2 divide-y text-xs">
            {readonly.map((p) => (
              <div key={p.key} className="py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px]">{p.key}</span>
                  <Badge variant={p.disabled ? "warning" : "muted"}>{p.disabled ? p.disabled_reason ?? "只读" : "只读"}</Badge>
                </div>
                <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">{p.note}</p>
              </div>
            ))}
            {readonly.length === 0 && <div className="py-3 text-xs text-muted-foreground">加载中…</div>}
          </div>
        </section>
      </aside>

      {confirmOpen && preview && (
        <Modal
          title="确认执行清理"
          subtitle="归档完成后才会删除；此操作不可撤销。"
          onClose={() => setConfirmOpen(false)}
        >
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs leading-relaxed">
              即将删除 <strong>{policy?.label}</strong>，预计 <strong>{preview.files != null ? `${preview.files} 个文件` : `${fmtRows(preview.rows)} 行`}</strong>。截止时间 {preview.before.slice(0, 16).replace("T", " ")}。
            </div>
            <div>
              <Label>输入确认词</Label>
              <Input
                className="mt-1 font-mono"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={confirmPhrase}
                autoComplete="off"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">必须完全一致：<code>{confirmPhrase}</code></p>
            </div>
            {err && <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>取消</Button>
              <Button
                variant="destructive"
                disabled={confirmText !== confirmPhrase || executeMut.isPending}
                onClick={() => executeMut.mutate()}
              >
                {executeMut.isPending ? "归档并删除中…" : "归档并删除"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
